import { z } from 'zod';
import { TRIP_STATES } from '../trips/trip-transitions';

/** Trip model (PRD → Core objects → Trip; gitaumoses4/chukua#41): one truck, one trailer, one driver, one leg of a job. */

export const TRIP_LEG_TYPES = ['delivery', 'empty_return', 'transfer'] as const;
export type TripLegType = (typeof TRIP_LEG_TYPES)[number];

/**
 * `pending` is a trip with no events yet, the column's word for "no state". `assigned` has a truck, trailer and driver but no
 * state-changing event yet (gitaumoses4/chukua#321); it is set by assignment, not by the log. Every other status is a `TripState`,
 * derived from the event log by `reduceTripState` and kept on the trip (gitaumoses4/chukua#44).
 */
export const TRIP_STATUSES = ['pending', 'assigned', ...TRIP_STATES] as const;
export type TripStatus = (typeof TRIP_STATUSES)[number];

/**
 * The statuses in which a trip holds its truck, trailer and driver: from assignment until close-out. The partial unique indexes on `trips`
 * and the "on another open trip" check both read this list, so a resource is held by one trip at a time. Not in it: `aborted` (the
 * driver is stood down), `close_out` and `ready_to_invoice` (the legs are done).
 */
export const OPEN_TRIP_STATUSES = [
  'assigned',
  'dispatched',
  'pre_trip_failed',
  'in_transit',
  'exception',
  'delivered',
] as const satisfies readonly TripStatus[];

/**
 * One trip's cargo, for the leg it names: the containers it carries (container jobs) or a loose
 * quantity (loose jobs). Which of the two a job needs depends on its job type, so that cross-check
 * is `trip-planning.policy.ts`'s, not this schema's. Several containers make one trip; three
 * trips for three containers are three calls.
 */
export const createTripInputSchema = z
  .object({
    legType: z.enum(TRIP_LEG_TYPES),
    containerIds: z.array(z.uuid()).min(1).max(20).optional(),
    quantity: z.number().int().positive().optional(),
  })
  .refine((input) => !(input.containerIds && input.quantity !== undefined), { message: 'A trip carries containers or a loose quantity, not both' })
  .refine((input) => !input.containerIds || new Set(input.containerIds).size === input.containerIds.length, { message: 'A container can only be listed once' });
export type CreateTripInput = z.infer<typeof createTripInputSchema>;

/**
 * A trip's crew: truck and driver always, the trailer when the dispatcher names one. Left out, the trailer is the one coupled to the
 * truck now (gitaumoses4/chukua#390), and a truck whose class tows nothing takes none. Re-sending the resolved crew is a no-op, other ids a
 * reassignment. `alsoCouple` makes a named trailer the truck's coupling too, in the same transaction; `dropZoneCode` says where the truck's
 * current trailer is left when that happens.
 */
export const assignTripInputSchema = z
  .object({
    truckId: z.uuid(),
    trailerId: z.uuid().optional(),
    driverId: z.uuid(),
    alsoCouple: z.boolean().optional(),
    dropZoneCode: z.string().trim().min(1).max(64).optional(),
  })
  .refine((input) => !input.alsoCouple || input.trailerId, { message: 'alsoCouple needs a trailerId', path: ['alsoCouple'] })
  .refine((input) => !input.dropZoneCode || input.alsoCouple, { message: 'dropZoneCode only applies with alsoCouple', path: ['dropZoneCode'] });
export type AssignTripInput = z.infer<typeof assignTripInputSchema>;

/**
 * `PUT /trips/:id/assignment`: the crew, plus the window the suggestions were asked for (gitaumoses4/chukua#52). The window is what the
 * picker showed the dispatcher, so the record of how the chosen crew ranked is for the same list; left out, the next 24 hours.
 */
export const assignTripRequestSchema = assignTripInputSchema
  .extend({
    windowStart: z.coerce.date().optional(),
    windowEnd: z.coerce.date().optional(),
  })
  .refine((input) => (input.windowStart === undefined) === (input.windowEnd === undefined), { message: 'Give both ends of the window or neither' })
  .refine((input) => !input.windowStart || !input.windowEnd || input.windowEnd > input.windowStart, { message: 'The window must end after it starts' });
export type AssignTripRequest = z.infer<typeof assignTripRequestSchema>;
